import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, CircularProgress, Tab, Tabs } from "@mui/material";

import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import BulkConfigIO from "components/BulkConfigIO";
import { protectedResources, siteData } from "authConfig";

import ConsequenceTablePanel from "./ConsequenceTablePanel";
import PlausibilityTablePanel from "./PlausibilityTablePanel";
import PrioritisationMatrixPanel from "./PrioritisationMatrixPanel";

const API = `${protectedResources.apiTodoList.endpoint}/evaluation-criteria/config`;
const BULK = `${protectedResources.apiTodoList.endpoint}/bulk-config/evaluation-criteria`;

export default function RiskEvaluationCriteria() {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  });

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Consequence Table state
  const [cLevels, setCLevels] = useState([]);
  const [cRows, setCRows] = useState([]);

  // Plausibility Table state — separate data per mode (ID172) so toggling shows only that mode's rows.
  const [pRows, setPRows] = useState([]); // timeframe_velocity rows
  const [lRows, setLRows] = useState([]); // likelihood bands
  // ID172 — plausibility mode: 'timeframe_velocity' | 'likelihood'
  const [pMode, setPMode] = useState("timeframe_velocity");
  // The rows the active mode edits (panel + matrix Y-axis).
  const activeRows = pMode === "likelihood" ? lRows : pRows;
  const setActiveRows = pMode === "likelihood" ? setLRows : setPRows;

  // Prioritisation Matrix state — structure derived from cLevels / pRows
  const [matrixYLabel, setMatrixYLabel] = useState("Plausibility");
  const [matrixCells, setMatrixCells] = useState({});

  // Toggling mode also relabels the prioritisation-matrix Y-axis.
  const handleModeChange = (m) => {
    setPMode(m);
    setMatrixYLabel(m === "likelihood" ? "Likelihood" : "Plausibility");
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await executeRef.current("GET", API);
      const data = res?.data ?? res;
      setCLevels(data?.consequenceTable?.levels || []);
      setCRows(data?.consequenceTable?.rows || []);
      setPRows(data?.plausibilityTable?.rows || []);
      setLRows(data?.likelihoodTable?.rows || []);
      const mode = data?.plausibilityMode === "likelihood" ? "likelihood" : "timeframe_velocity";
      setPMode(mode);
      const pm = data?.prioritisationMatrix;
      setMatrixYLabel(mode === "likelihood" ? "Likelihood" : pm?.yLabel || "Plausibility");
      const cellMap = {};
      (pm?.rows || []).forEach((row) => {
        (row.cells || []).forEach((cell) => {
          cellMap[`${row.id}:${cell.colId}`] = {
            rating: cell.rating || "",
            color: cell.color || "#ffffff",
          };
        });
      });
      setMatrixCells(cellMap);
    } catch {
      setError("Could not load Risk Evaluation Criteria config.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await executeRef.current("PUT", API, {
        plausibilityMode: pMode,
        consequenceTable: { levels: cLevels, rows: cRows },
        plausibilityTable: { rows: pRows },
        likelihoodTable: { rows: lRows },
        prioritisationMatrix: {
          xLabel: "Consequence",
          yLabel: matrixYLabel,
          columns: cLevels.map((l) => ({ id: l.id, order: l.order, label: l.label })),
          // Y-axis rows come from the ACTIVE mode's plausibility data.
          rows: activeRows.map((pr) => ({
            id: pr.id,
            order: pr.order,
            label: pr.rating,
            cells: cLevels.map((l) => {
              const cell = matrixCells[`${pr.id}:${l.id}`];
              return { colId: l.id, rating: cell?.rating || "", color: cell?.color || "#ffffff" };
            }),
          })),
        },
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError("Save failed. Please try again.");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={3} py={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <MDTypography variant="h5" fontWeight="bold">
            Risk Evaluation Criteria
          </MDTypography>
          <Box display="flex" gap={1} alignItems="center">
            {/* Bulk edit: download the current evaluation criteria as .xlsx, edit, re-import with a preview. */}
            <BulkConfigIO
              title="Risk Evaluation Criteria"
              exportUrl={`${BULK}/export`}
              previewUrl={`${BULK}/preview`}
              applyUrl={`${BULK}/apply`}
              filename="risk-evaluation-criteria.xlsx"
              onApplied={load}
              helpText="The file is the full config across all sheets: keep an ID to modify a row, blank it to add, remove a row to delete it. The Settings sheet picks the active plausibility mode."
            />
            {/* Discard — revert any unsaved edits back to the last saved config. */}
            <MDButton
              variant="outlined"
              disabled={saving}
              onClick={load}
              sx={{
                color: "#52009b",
                borderColor: "#52009b",
                textTransform: "none",
                "&:hover": { borderColor: "#3a0070", backgroundColor: "rgba(82,0,155,0.06)" },
              }}
            >
              Discard
            </MDButton>
            <MDButton
              variant="contained"
              disabled={saving}
              onClick={handleSave}
              sx={{
                color: "#f7f4fb",
                backgroundColor: "#52009b",
                textTransform: "none",
                "&:hover": { backgroundColor: "#3d0075" },
              }}
            >
              {saving ? "Saving…" : "Save"}
            </MDButton>
          </Box>
        </Box>

        {error && (
          <MDAlert color="error" dismissible onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </MDAlert>
        )}
        {success && (
          <MDAlert color="success" dismissible onClose={() => setSuccess(false)} sx={{ mb: 2 }}>
            Saved successfully.
          </MDAlert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="Consequence Table" />
            <Tab label="Plausibility Table" />
            <Tab label="Prioritisation Matrix" />
          </Tabs>
        </Box>

        {tab === 0 && (
          <ConsequenceTablePanel
            levels={cLevels}
            setLevels={setCLevels}
            rows={cRows}
            setRows={setCRows}
          />
        )}
        {tab === 1 && (
          <PlausibilityTablePanel
            rows={activeRows}
            setRows={setActiveRows}
            mode={pMode}
            setMode={handleModeChange}
          />
        )}
        {tab === 2 && (
          <PrioritisationMatrixPanel
            yLabel={matrixYLabel}
            columns={cLevels}
            rows={activeRows}
            cells={matrixCells}
            setCells={setMatrixCells}
          />
        )}
      </MDBox>
    </DashboardLayout>
  );
}
